import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged, User, signInWithEmailAndPassword } from 'firebase/auth';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { Question, Category, Feedback, Resource, AppConfig, UserProfile, Page } from '../types';
import { Markdown } from './Markdown';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Edit2, Save, X, LogOut, ChevronDown, ChevronUp, ChevronRight, ExternalLink, Info, Eye, EyeOff, GripVertical, Loader2, Sparkles, Layout, ArrowLeft, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableQuestionProps {
  q: Question;
  editingQ: string | null;
  editingDesc: string | null;
  editingFeedbackQ: string | null;
  setEditingQ: (id: string | null) => void;
  setEditingDesc: (id: string | null) => void;
  setEditingFeedbackQ: (id: string | null) => void;
  saveQuestionEdit: (id: string, text: string, description?: string, scoreFeedback?: any) => void;
  deleteQuestion: (id: string) => void;
  toggleShadow: (id: string, current: boolean) => void;
}

const SortableQuestion: React.FC<SortableQuestionProps> = ({
  q,
  editingQ,
  editingDesc,
  editingFeedbackQ,
  setEditingQ,
  setEditingDesc,
  setEditingFeedbackQ,
  saveQuestionEdit,
  deleteQuestion,
  toggleShadow,
}) => {
  const [localDesc, setLocalDesc] = useState(q.description || '');
  const [localScoreFeedback, setLocalScoreFeedback] = useState(q.scoreFeedback || {});

  useEffect(() => {
    setLocalDesc(q.description || '');
  }, [q.description]);

  useEffect(() => {
    setLocalScoreFeedback(q.scoreFeedback || {});
  }, [q.scoreFeedback]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: q.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "bg-bg-card border border-border-main rounded-xl p-4 flex flex-col gap-3 group hover:border-border-focus transition-all",
        (q.isShadow || q.category.startsWith('shadow_')) && "border-dashed border-cat-responsibility/30"
      )}
    >
      <div className="flex items-center gap-4">
        <span {...attributes} {...listeners} className="text-text-muted cursor-grab active:cursor-grabbing p-1 hover:text-text-main transition-colors">
          <GripVertical size={16} />
        </span>
        {editingQ === q.id ? (
          <input
            autoFocus
            className="flex-1 bg-transparent border-b border-border-main text-text-main text-sm py-1 outline-none focus:border-border-focus"
            defaultValue={q.text}
            onBlur={(e) => saveQuestionEdit(q.id, e.target.value, q.description)}
            onKeyDown={(e) => e.key === 'Enter' && saveQuestionEdit(q.id, e.currentTarget.value, q.description)}
          />
        ) : (
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <Markdown className="markdown-body text-sm leading-relaxed text-text-main flex-1 min-w-0" content={q.text} />
            {(q.isShadow || q.category.startsWith('shadow_')) && (
              <span className="text-[0.5rem] tracking-widest uppercase px-1.5 py-0.5 bg-cat-responsibility/10 text-cat-responsibility rounded border border-cat-responsibility/20">Shadow</span>
            )}
          </div>
        )}
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => toggleShadow(q.id, !!(q.isShadow || q.category.startsWith('shadow_')))}
            className={cn(
              "p-2 rounded-lg transition-all",
              (q.isShadow || q.category.startsWith('shadow_')) ? "text-cat-responsibility bg-cat-responsibility/10 hover:bg-cat-responsibility/20" : "text-text-muted hover:text-text-main hover:bg-bg-main"
            )}
            title={(q.isShadow || q.category.startsWith('shadow_')) ? "Shadow Question (Excluded from category score)" : "Regular Question"}
          >
            {(q.isShadow || q.category.startsWith('shadow_')) ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          <button
            onClick={() => setEditingFeedbackQ(editingFeedbackQ === q.id ? null : q.id)}
            className={cn(
              "p-2 text-text-muted hover:text-text-main hover:bg-bg-main rounded-lg transition-all",
              editingFeedbackQ === q.id && "bg-bg-main text-text-main"
            )}
            title="Manage Score Feedback"
          >
            <ChevronDown size={14} className={cn("transition-transform", editingFeedbackQ === q.id && "rotate-180")} />
          </button>
          <button onClick={() => setEditingQ(q.id)} className="p-2 text-text-muted hover:text-text-main hover:bg-bg-main rounded-lg transition-all">
            <Edit2 size={14} />
          </button>
          <button onClick={() => deleteQuestion(q.id)} className="p-2 text-text-muted hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="pl-8 space-y-4">
        {editingDesc === q.id ? (
          <textarea
            value={localDesc}
            onChange={(e) => setLocalDesc(e.target.value)}
            onBlur={() => saveQuestionEdit(q.id, q.text, localDesc, q.scoreFeedback)}
            placeholder="Add a detailed description..."
            className="w-full min-h-[100px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
          />
        ) : (
          <div className="flex items-start gap-2 group/desc">
            <div className={cn("text-xs leading-relaxed transition-colors", q.description ? "text-text-muted" : "text-text-muted/60 italic")}>
              {q.description ? <Markdown className="markdown-body" content={q.description} /> : "No description added."}
            </div>
            <button onClick={() => setEditingDesc(q.id)} className="opacity-0 group-hover/desc:opacity-100 p-1 text-text-muted/60 hover:text-text-muted transition-all">
              <Edit2 size={10} />
            </button>
          </div>
        )}

        {editingFeedbackQ === q.id && (
          <div className="bg-bg-main border border-border-secondary rounded-xl p-4 space-y-6">
            <p className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted font-bold">Score-Specific Feedback (1-5)</p>
            <div className="space-y-6">
              {[1, 2, 3, 4, 5].map(score => (
                <div key={score} className="space-y-3 p-3 border border-border-main rounded-lg bg-bg-card">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-border-secondary text-text-main text-[0.65rem] flex items-center justify-center font-bold">{score}</span>
                    <span className="text-[0.6rem] tracking-widest uppercase text-text-muted">Feedback & Action</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[0.55rem] tracking-widest uppercase text-text-muted">Feedback</label>
                      <textarea
                        value={localScoreFeedback[score]?.feedback || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          const newFeedback = { ...localScoreFeedback };
                          newFeedback[score] = { ...(newFeedback[score] || { feedback: '', action: '' }), feedback: val };
                          setLocalScoreFeedback(newFeedback);
                        }}
                        onBlur={() => saveQuestionEdit(q.id, q.text, q.description, localScoreFeedback)}
                        placeholder="What does this score mean?"
                        className="w-full min-h-[100px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[0.55rem] tracking-widest uppercase text-cat-responsibility">Action</label>
                      <textarea
                        value={localScoreFeedback[score]?.action || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          const newFeedback = { ...localScoreFeedback };
                          newFeedback[score] = { ...(newFeedback[score] || { feedback: '', action: '' }), action: val };
                          setLocalScoreFeedback(newFeedback);
                        }}
                        onBlur={() => saveQuestionEdit(q.id, q.text, q.description, localScoreFeedback)}
                        placeholder="What should they do next?"
                        className="w-full min-h-[100px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PageEditor: React.FC<{ 
  pageId: string; 
  page: Page | undefined; 
  savePage: (id: string, title: string, content: string) => void; 
}> = ({ pageId, page, savePage }) => {
  const [localTitle, setLocalTitle] = useState(page?.title || '');
  const [localContent, setLocalContent] = useState(page?.content || '');

  useEffect(() => {
    if (page) {
      setLocalTitle(page.title);
      setLocalContent(page.content);
    }
  }, [page]);

  return (
    <div className="space-y-4 p-6 border border-border-main rounded-xl bg-bg-main/50">
      <div className="flex justify-between items-center">
        <h3 className="text-[0.65rem] tracking-[0.2em] uppercase text-cat-overall font-bold">{pageId.replace('-', ' ')} Page</h3>
        {page && (
          <span className="text-[0.55rem] text-text-muted tracking-widest uppercase">
            Last updated: {page.updatedAt?.toDate().toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-[0.55rem] tracking-widest uppercase text-text-muted">Page Title</label>
          <input 
            type="text"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            onBlur={() => savePage(pageId, localTitle, localContent)}
            placeholder="Page Title"
            className="w-full bg-bg-input border border-border-secondary rounded-lg px-4 py-2 text-sm text-text-main outline-none focus:border-border-focus transition-all"
          />
        </div>

        <div className="space-y-2">
          <label className="text-[0.55rem] tracking-widest uppercase text-text-muted">Content (Markdown)</label>
          <textarea 
            value={localContent}
            onChange={(e) => setLocalContent(e.target.value)}
            onBlur={() => savePage(pageId, localTitle, localContent)}
            placeholder="Write page content in Markdown..."
            className="w-full min-h-[800px] bg-bg-input border border-border-secondary rounded-lg p-4 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y font-mono"
          />
        </div>
      </div>
    </div>
  );
};

export const AdminDashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [appConfig, setAppConfig] = useState<AppConfig>({ 
    aboutText: '', 
    acknowledgmentText: '',
    howToReadText: '',
    categoryDescriptions: { 
      responsibility: '', selftrust: '', care: '', overall: ''
    },
    categoryShortDescriptions: { 
      responsibility: '', selftrust: '', care: '', overall: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'settings' | 'landing' | 'pages' | 'questions' | 'feedback'>('settings');
  const [showLandingPreviews, setShowLandingPreviews] = useState(false);
  const [activeTab, setActiveTab] = useState<Category>('responsibility');
  const [editingQ, setEditingQ] = useState<string | null>(null);
  const [editingDesc, setEditingDesc] = useState<string | null>(null);
  const [editingFeedbackQ, setEditingFeedbackQ] = useState<string | null>(null);
  const [newQText, setNewQText] = useState<Record<Category, string>>({ 
    responsibility: '', selftrust: '', care: '', overall: '',
    shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
  });
  const [newQDesc, setNewQDesc] = useState<Record<Category, string>>({ 
    responsibility: '', selftrust: '', care: '', overall: '',
    shadow_responsibility: '', shadow_selftrust: '', shadow_care: ''
  });
  const [openBands, setOpenBands] = useState<Record<string, boolean>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'landing-config': true,
    'static-pages': true,
    'questions-responsibility': true,
    'questions-selftrust': true,
    'questions-care': true,
    'score-feedback': true
  });

  const toggleSection = (id: string) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = questions.findIndex((q) => q.id === active.id);
    const newIndex = questions.findIndex((q) => q.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newQuestions: Question[] = arrayMove(questions, oldIndex, newIndex);
    
    // Optimistically update local state
    setQuestions(newQuestions);

    // Update Firestore for all affected questions
    try {
      const batch = writeBatch(db);
      let hasChanges = false;
      newQuestions.forEach((q, index) => {
        if (q.order !== index) {
          batch.update(doc(db, 'questions', q.id), { order: index });
          hasChanges = true;
        }
      });

      if (hasChanges) {
        await batch.commit();
      }
    } catch (error) {
      console.error("Error updating question order:", error);
      handleFirestoreError(error, OperationType.UPDATE, 'questions');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        // Use onSnapshot to be reactive to role changes (e.g. from App.tsx sync)
        const profileUnsub = onSnapshot(doc(db, 'users', u.uid), (snap) => {
          if (snap.exists()) {
            setUserProfile(snap.data() as UserProfile);
          }
          setLoading(false);
        }, (error) => {
          console.error("Error listening to user profile:", error);
          setLoading(false);
        });
        return () => profileUnsub();
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!userProfile || userProfile.role !== 'admin') return;

    const qUnsub = onSnapshot(query(collection(db, 'questions'), orderBy('order')), (snap) => {
      setQuestions(snap.docs.map(d => ({ ...d.data(), id: d.id } as Question)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'questions');
    });

    const fUnsub = onSnapshot(collection(db, 'feedback'), (snap) => {
      setFeedback(snap.docs.map(d => ({ ...d.data(), id: d.id } as Feedback)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'feedback');
    });

    const cUnsub = onSnapshot(doc(db, 'config', 'app'), (snap) => {
      if (snap.exists()) {
        const data = snap.data() as AppConfig;
        if (!data.categoryDescriptions) {
          data.categoryDescriptions = { responsibility: '', selftrust: '', care: '', overall: '' };
        }
        if (!data.categoryShortDescriptions) {
          data.categoryShortDescriptions = { responsibility: '', selftrust: '', care: '', overall: '' };
        }
        setAppConfig(data);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'config/app');
    });

    const pUnsub = onSnapshot(collection(db, 'pages'), (snap) => {
      setPages(snap.docs.map(d => ({ ...d.data(), id: d.id } as Page)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'pages');
    });

    return () => {
      qUnsub();
      fUnsub();
      cUnsub();
      pUnsub();
    };
  }, [user]);

  const getAuthErrorMessage = (code: string) => {
    switch (code) {
      case 'auth/user-not-found': return "No account found with this email.";
      case 'auth/wrong-password': return "Incorrect password. Please try again.";
      case 'auth/invalid-email': return "Please enter a valid email address.";
      case 'auth/too-many-requests': return "Too many failed attempts. Please try again later.";
      case 'auth/user-disabled': return "This account has been disabled.";
      case 'auth/popup-closed-by-user': return "Sign-in popup was closed before completion.";
      case 'auth/operation-not-allowed': return "Email/Password sign-in is not enabled. Please contact support.";
      case 'auth/network-request-failed': return "Network error. Please check your internet connection.";
      case 'auth/invalid-credential': return "Invalid login credentials. Please check your email and password.";
      default: return "An unexpected error occurred. Please try again.";
    }
  };

  const login = async () => {
    setAuthError(null);
    setIsAuthLoading(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err: any) {
      console.error("Login failed:", err);
      setAuthError(getAuthErrorMessage(err.code));
    } finally {
      setIsAuthLoading(false);
    }
  };

  const loginWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsAuthLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      setAuthError(getAuthErrorMessage(err.code));
    } finally {
      setIsAuthLoading(false);
    }
  };
  const logout = () => signOut(auth);

  const addQuestion = async (cat: Category) => {
    const text = newQText[cat].trim();
    const description = newQDesc[cat].trim();
    if (!text) return;
    try {
      const maxOrder = questions.length > 0 ? Math.max(...questions.map(q => q.order || 0)) : 0;
      await addDoc(collection(db, 'questions'), {
        category: cat,
        text,
        description: description || null,
        order: maxOrder + 1,
        isShadow: false,
        scoreFeedback: {
          1: { feedback: '', action: '' },
          2: { feedback: '', action: '' },
          3: { feedback: '', action: '' },
          4: { feedback: '', action: '' },
          5: { feedback: '', action: '' }
        }
      });
      setNewQText(prev => ({ ...prev, [cat]: '' }));
      setNewQDesc(prev => ({ ...prev, [cat]: '' }));
    } catch (e) { console.error(e); }
  };

  const deleteQuestion = async (id: string) => {
    try { await deleteDoc(doc(db, 'questions', id)); } catch (e) { console.error(e); }
  };

  const saveQuestionEdit = async (id: string, text: string, description?: string, scoreFeedback?: any) => {
    try {
      const updateData: any = { text, description: description || null };
      if (scoreFeedback) updateData.scoreFeedback = scoreFeedback;
      await updateDoc(doc(db, 'questions', id), updateData);
      setEditingQ(null);
      setEditingDesc(null);
    } catch (e) { console.error(e); }
  };

  const toggleShadow = async (id: string, current: boolean) => {
    try {
      await updateDoc(doc(db, 'questions', id), { isShadow: !current });
    } catch (e) { 
      console.error(e);
      handleFirestoreError(e, OperationType.UPDATE, `questions/${id}`);
    }
  };

  const toggleBand = (id: string) => {
    setOpenBands(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const saveFeedback = async (id: string | undefined, data: Partial<Feedback>) => {
    try {
      if (id) {
        await updateDoc(doc(db, 'feedback', id), data);
      } else {
        await addDoc(collection(db, 'feedback'), data);
      }
    } catch (e) { console.error(e); }
  };

  const deleteFeedback = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'feedback', id));
    } catch (e) { console.error(e); }
  };

  const addFeedbackRange = async (cat: Category) => {
    try {
      const baseData: any = {
        category: cat,
        minScore: 0,
        maxScore: cat === 'overall' ? 15 : 5,
        title: cat === 'overall' ? 'New Overall Range' : '',
        text: '',
        levelUpText: '',
        resources: []
      };

      if (cat === 'overall') {
        baseData.responsibilityMin = 0;
        baseData.responsibilityMax = 5;
        baseData.selftrustMin = 0;
        baseData.selftrustMax = 5;
        baseData.careMin = 0;
        baseData.careMax = 5;
      }

      await addDoc(collection(db, 'feedback'), baseData);
    } catch (e) { console.error(e); }
  };

  const saveAppField = async (field: string, value: any) => {
    try {
      await updateDoc(doc(db, 'config', 'app'), { [field]: value });
    } catch (e) { 
      console.error(e);
      // If document doesn't exist, try setDoc
      try {
        await setDoc(doc(db, 'config', 'app'), { [field]: value }, { merge: true });
      } catch (e2) { console.error(e2); }
    }
  };

  const savePage = async (id: string, title: string, content: string) => {
    try {
      await setDoc(doc(db, 'pages', id), {
        title,
        content,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (e) {
      console.error("Error saving page:", e);
      handleFirestoreError(e, OperationType.WRITE, `pages/${id}`);
    }
  };

  const deletePage = async (id: string) => {
    if (!window.confirm(`Are you sure you want to delete the ${id} page?`)) return;
    try {
      await deleteDoc(doc(db, 'pages', id));
    } catch (e) {
      console.error("Error deleting page:", e);
      handleFirestoreError(e, OperationType.DELETE, `pages/${id}`);
    }
  };

  const saveCategoryDescription = async (id: Category, description: string) => {
    try {
      const newDescriptions = { ...appConfig.categoryDescriptions, [id]: description };
      await updateDoc(doc(db, 'config', 'app'), { categoryDescriptions: newDescriptions });
    } catch (e) { console.error(e); }
  };

  const saveCategoryShortDescription = async (id: Category, description: string) => {
    try {
      const newShortDescriptions = { ...appConfig.categoryShortDescriptions, [id]: description };
      await updateDoc(doc(db, 'config', 'app'), { categoryShortDescriptions: newShortDescriptions });
    } catch (e) { console.error(e); }
  };

  const seedDatabase = async () => {
    if (import.meta.env.PROD) {
      console.warn("Seeding is disabled in production.");
      return;
    }
    const DEFAULT_QUESTIONS = [
      { id: 'q1', category: 'responsibility', text: 'I take ownership of my decisions and their consequences.', order: 1 },
      { id: 'q2', category: 'responsibility', text: 'I follow through on the commitments I make to myself and others.', order: 2 },
      { id: 'q3', category: 'responsibility', text: 'When things go wrong, I look at my own role in the outcome.', order: 3 },
      { id: 'q4', category: 'responsibility', text: 'I proactively address problems rather than waiting for others to fix them.', order: 4 },
      { id: 'q5', category: 'responsibility', text: 'I am honest with myself about the impact my choices have on those around me.', order: 5 },
      { id: 'q6', category: 'selftrust', text: 'I trust my instincts when making important decisions.', order: 6 },
      { id: 'q7', category: 'selftrust', text: 'I believe in my own ability to navigate challenges.', order: 7 },
      { id: 'q8', category: 'selftrust', text: 'I act on my inner knowing even when others disagree.', order: 8 },
      { id: 'q9', category: 'selftrust', text: 'I recover my confidence quickly after setbacks or criticism.', order: 9 },
      { id: 'q10', category: 'selftrust', text: 'I make decisions without needing excessive reassurance from others.', order: 10 },
      { id: 'q11', category: 'care', text: 'I treat myself with compassion when I make mistakes.', order: 11 },
      { id: 'q12', category: 'care', text: 'I consider how my actions affect the people around me.', order: 12 },
      { id: 'q13', category: 'care', text: 'I nurture my own wellbeing as a priority, not an afterthought.', order: 13 },
      { id: 'q14', category: 'care', text: 'I set boundaries that protect my energy and values.', order: 14 },
      { id: 'q15', category: 'care', text: 'I approach difficult conversations with empathy rather than judgement.', order: 15 },
    ];

    const DEFAULT_CATEGORIES = [
      { id: 'responsibility', description: 'How much you own your choices and outcomes.', shortDescription: 'Ownership of choices and outcomes.' },
      { id: 'selftrust', description: 'Confidence in your own judgement and inner compass.', shortDescription: 'Confidence in your own judgement.' },
      { id: 'care', description: 'Compassion toward yourself and others as you act.', shortDescription: 'Compassion toward yourself and others.' },
      { id: 'overall', description: 'Your total agency score across all dimensions.', shortDescription: 'Total agency score across all dimensions.' }
    ];

    try {
      if (import.meta.env.DEV) console.log("Seeding questions...");
      for (const q of DEFAULT_QUESTIONS) {
        await setDoc(doc(db, 'questions', q.id), q);
      }
      
      if (import.meta.env.DEV) console.log("Seeding categories...");
      const categoryDescriptions: Record<string, string> = {};
      const categoryShortDescriptions: Record<string, string> = {};
      for (const cat of DEFAULT_CATEGORIES) {
        categoryDescriptions[cat.id] = cat.description;
        categoryShortDescriptions[cat.id] = cat.shortDescription;
      }
      await updateDoc(doc(db, 'config', 'app'), { categoryDescriptions, categoryShortDescriptions });
      
      if (import.meta.env.DEV) console.log("Seeding static pages...");
      const DEFAULT_PAGES = [
        { id: 'about', title: 'About Us', content: '# About Us\n\nWelcome to The Insourcing Way...' },
        { id: 'privacy', title: 'Privacy Policy', content: '# Privacy Policy\n\nYour privacy is important to us...' },
        { id: 'terms', title: 'Terms of Service', content: '# Terms of Service\n\nBy using our service, you agree to...' }
      ];
      for (const p of DEFAULT_PAGES) {
        await setDoc(doc(db, 'pages', p.id), {
          title: p.title,
          content: p.content,
          updatedAt: Timestamp.now()
        });
      }

      if (import.meta.env.DEV) console.log("Seeding complete.");
    } catch (e) { 
      console.error("Seeding failed:", e);
      // Try to determine which collection failed for better debugging
      const path = (e as any)?.path || 'unknown';
      handleFirestoreError(e, OperationType.WRITE, path);
    }
  };

  if (loading) return <div className="min-h-screen bg-bg-main flex items-center justify-center text-text-muted uppercase tracking-widest text-xs">Verifying Access...</div>;

  if (!userProfile || userProfile.role !== 'admin') {
    return (
      <div className="min-h-screen bg-bg-main flex flex-col items-center justify-center p-6 gap-8">
        <h1 className="text-[0.75rem] tracking-[0.35em] uppercase text-text-muted font-light">Agency — Admin</h1>
        <div className="bg-bg-card border border-border-secondary rounded-2xl p-8 w-full max-w-sm flex flex-col gap-6 shadow-2xl">
          <p className="text-[0.75rem] text-text-muted tracking-widest uppercase text-center">Administrator access</p>
          
          {!showEmailLogin ? (
            <div className="space-y-4">
              <button 
                onClick={login} 
                disabled={isAuthLoading}
                className="w-full py-3 bg-bg-main border border-border-secondary rounded-xl text-text-main hover:bg-bg-input transition-all tracking-widest text-xs uppercase disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isAuthLoading ? <Loader2 size={16} className="animate-spin" /> : "Sign in with Google"}
              </button>
              <button 
                onClick={() => setShowEmailLogin(true)}
                className="w-full text-[0.6rem] tracking-[0.2em] uppercase text-text-muted hover:text-text-secondary transition-all"
              >
                Or use email and password
              </button>
            </div>
          ) : (
            <form onSubmit={loginWithEmail} className="space-y-4">
              <div className="space-y-2">
                <input 
                  type="email" 
                  placeholder="Email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-bg-input border border-border-secondary rounded-lg px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                  required
                />
                <input 
                  type="password" 
                  placeholder="Password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-bg-input border border-border-secondary rounded-lg px-4 py-3 text-sm text-text-main outline-none focus:border-border-focus transition-all"
                  required
                />
              </div>
              
              {authError && <p className="text-red-500/80 text-[0.65rem] text-center italic">{authError}</p>}
              
              <button 
                type="submit" 
                disabled={isAuthLoading}
                className="w-full py-3 bg-bg-main border border-border-secondary rounded-xl text-text-main hover:bg-bg-input transition-all tracking-widest text-xs uppercase disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {isAuthLoading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
              </button>
              
              <button 
                type="button"
                onClick={() => {
                  setShowEmailLogin(false);
                  setAuthError(null);
                }}
                className="w-full text-[0.6rem] tracking-[0.2em] uppercase text-text-muted hover:text-text-secondary transition-all"
              >
                Back to Google Sign In
              </button>
            </form>
          )}

          {user && userProfile && userProfile.role !== 'admin' && <p className="text-red-500 text-xs text-center">Access denied. You are not an admin.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main text-text-main pb-20">
      <header className="flex items-center justify-between px-8 py-5 border-b border-border-main bg-bg-card sticky top-0 z-10">
        <div className="flex items-center gap-6">
          <Link to="/" className="p-2 bg-bg-card border border-border-secondary rounded-full text-text-muted hover:text-text-main transition-all">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="text-[0.72rem] tracking-[0.3em] uppercase text-text-muted font-light">Agency — Question Manager</h1>
        </div>
        <div className="flex gap-4 items-center">
          {!import.meta.env.PROD && questions.length === 0 && (
            <button onClick={seedDatabase} className="text-[0.72rem] tracking-widest uppercase text-cat-care border border-cat-care/30 px-4 py-2 rounded-lg hover:bg-cat-care/10 transition-all">Seed Questions</button>
          )}
          {!import.meta.env.PROD && (!appConfig.categoryDescriptions || Object.values(appConfig.categoryDescriptions).every(v => !v)) && (
            <button onClick={seedDatabase} className="text-[0.72rem] tracking-widest uppercase text-cat-responsibility border border-cat-responsibility/30 px-4 py-2 rounded-lg hover:bg-cat-responsibility/10 transition-all">Seed Categories</button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <nav className="flex gap-1 border-b border-border-main mb-12 overflow-x-auto no-scrollbar">
          {[
            { id: 'settings', label: 'Settings', icon: Settings },
            { id: 'landing', label: 'Landing', icon: Layout },
            { id: 'pages', label: 'Pages', icon: Edit2 },
            { id: 'questions', label: 'Questions', icon: Plus },
            { id: 'feedback', label: 'Feedback', icon: Sparkles },
          ].map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id as any)}
              className={cn(
                "flex items-center gap-2 px-6 py-4 text-[0.6rem] tracking-[0.25em] uppercase font-bold transition-all border-b-2 -mb-px whitespace-nowrap",
                activeSection === section.id 
                  ? "border-cat-overall text-text-main bg-bg-card" 
                  : "border-transparent text-text-muted hover:text-text-secondary hover:bg-bg-card"
              )}
            >
              <section.icon size={14} />
              {section.label}
            </button>
          ))}
        </nav>

        <div className="space-y-16">
          {activeSection === 'settings' && (
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Settings size={16} className="text-text-muted" />
                <h2 className="text-[0.68rem] tracking-[0.25em] uppercase font-bold text-text-main flex-1">App & PDF Settings</h2>
              </div>
              <div className="bg-bg-card border border-border-secondary rounded-xl p-6 space-y-6">
                <p className="text-xs text-text-secondary">Configure overall presentation and reporting behavior for the assessment.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                  {/* Option 1: Center Logo */}
                  <div className="flex flex-col justify-between p-4 bg-bg-main border border-border-secondary/60 rounded-xl space-y-4">
                    <div className="space-y-1">
                      <h4 className="text-[0.68rem] tracking-wider uppercase font-bold text-text-main">Center Logo in Venn Diagram</h4>
                      <p className="text-[0.65rem] text-text-muted">Display the brand logo mark in the overlap center of the main assessment Venn Diagram.</p>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-[0.65rem] tracking-widest uppercase text-text-muted font-bold">Status: {appConfig.showLogo !== false ? "Enabled" : "Disabled"}</span>
                      <button
                        onClick={() => {
                          const val = appConfig.showLogo !== false ? false : true;
                          setAppConfig(prev => ({ ...prev, showLogo: val }));
                          saveAppField('showLogo', val);
                        }}
                        className={cn(
                          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none",
                          appConfig.showLogo !== false ? "bg-success" : "bg-border-secondary"
                        )}
                      >
                        <span
                          className={cn(
                            "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                            appConfig.showLogo !== false ? "translate-x-4" : "translate-x-0"
                          )}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Option 2: Exclude Progress History */}
                  <div className="flex flex-col justify-between p-4 bg-bg-main border border-border-secondary/60 rounded-xl space-y-4">
                    <div className="space-y-1">
                      <h4 className="text-[0.68rem] tracking-wider uppercase font-bold text-text-main">PDF Progress History & Trend</h4>
                      <p className="text-[0.65rem] text-text-muted">Exclude the historic progress database trend and assessment logs from the generated client PDF reports.</p>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-[0.65rem] tracking-widest uppercase text-text-muted font-bold">Status: {appConfig.pdfExcludeHistory === true ? "Excluded" : "Included"}</span>
                      <button
                        onClick={() => {
                          const val = appConfig.pdfExcludeHistory === true ? false : true;
                          setAppConfig(prev => ({ ...prev, pdfExcludeHistory: val }));
                          saveAppField('pdfExcludeHistory', val);
                        }}
                        className={cn(
                          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none",
                          appConfig.pdfExcludeHistory === true ? "bg-success" : "bg-border-secondary"
                        )}
                      >
                        <span
                          className={cn(
                            "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                            appConfig.pdfExcludeHistory === true ? "translate-x-4" : "translate-x-0"
                          )}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeSection === 'landing' && (
            <section className="space-y-6">
          <button 
            onClick={() => toggleSection('landing-config')}
            className="flex items-center gap-3 w-full text-left hover:opacity-80 transition-opacity"
          >
            <Info size={16} className="text-text-muted" />
            <h2 className="text-[0.68rem] tracking-[0.25em] uppercase font-bold text-text-main flex-1">Landing Page Config</h2>
            <ChevronDown size={16} className={cn("text-text-muted transition-transform", !expandedSections['landing-config'] && "-rotate-90")} />
          </button>
          
          <AnimatePresence initial={false}>
            {expandedSections['landing-config'] && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="bg-bg-card border border-border-secondary rounded-xl p-6 space-y-8">
                  <div className="flex justify-end border-b border-border-secondary pb-4">
                    <button 
                      onClick={() => setShowLandingPreviews(!showLandingPreviews)}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-lg text-[0.6rem] tracking-widest uppercase font-bold transition-all border",
                        showLandingPreviews 
                          ? "bg-cat-overall/10 text-cat-overall border-cat-overall/40" 
                          : "bg-bg-input text-text-muted border-border-secondary hover:text-text-secondary"
                      )}
                    >
                      {showLandingPreviews ? <Eye size={12} /> : <EyeOff size={12} />}
                      {showLandingPreviews ? "Hide Previews" : "Show Previews"}
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">"About the Test" Text</label>
                    <textarea 
                      value={appConfig.aboutText} 
                      onChange={(e) => setAppConfig(prev => ({ ...prev, aboutText: e.target.value }))}
                      onBlur={() => saveAppField('aboutText', appConfig.aboutText)}
                      placeholder="Explain what the assessment is about for non-signed-in users..." 
                      className="w-full min-h-[150px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                    />
                    {showLandingPreviews && appConfig.aboutText && (
                      <div className="mt-2 p-3 bg-bg-main border border-border-secondary rounded-lg">
                        <p className="text-[0.55rem] tracking-widest uppercase text-text-muted mb-2">Preview</p>
                        <Markdown className="markdown-body text-xs text-text-secondary leading-relaxed" content={appConfig.aboutText} />
                      </div>
                    )}
                    <p className="text-[0.6rem] text-text-muted tracking-widest italic">This text is shown on the dashboard when a user is not signed in.</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">Acknowledgment & Thanks</label>
                    <textarea 
                      value={appConfig.acknowledgmentText || ''} 
                      onChange={(e) => setAppConfig(prev => ({ ...prev, acknowledgmentText: e.target.value }))}
                      onBlur={() => saveAppField('acknowledgmentText', appConfig.acknowledgmentText || '')}
                      placeholder="Thank the user for taking the test..." 
                      className="w-full min-h-[120px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                    />
                    {showLandingPreviews && appConfig.acknowledgmentText && (
                      <div className="mt-2 p-3 bg-bg-main border border-border-secondary rounded-lg">
                        <p className="text-[0.55rem] tracking-widest uppercase text-text-muted mb-2">Preview</p>
                        <Markdown className="markdown-body text-xs text-text-secondary leading-relaxed" content={appConfig.acknowledgmentText} />
                      </div>
                    )}
                    <p className="text-[0.6rem] text-text-muted tracking-widest italic">Shown under the personalized feedback section.</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">How to Read Results</label>
                    <textarea 
                      value={appConfig.howToReadText || ''} 
                      onChange={(e) => setAppConfig(prev => ({ ...prev, howToReadText: e.target.value }))}
                      onBlur={() => saveAppField('howToReadText', appConfig.howToReadText || '')}
                      placeholder="Explain how to interpret the scores and insights..." 
                      className="w-full min-h-[120px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                    />
                    {showLandingPreviews && appConfig.howToReadText && (
                      <div className="mt-2 p-3 bg-bg-main border border-border-secondary rounded-lg">
                        <p className="text-[0.55rem] tracking-widest uppercase text-text-muted mb-2">Preview</p>
                        <Markdown className="markdown-body text-xs text-text-secondary leading-relaxed" content={appConfig.howToReadText} />
                      </div>
                    )}
                    <p className="text-[0.6rem] text-text-muted tracking-widest italic">Shown under the personalized feedback section.</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
        )}

        {activeSection === 'pages' && (
        <section className="space-y-6">
          <button 
            onClick={() => toggleSection('static-pages')}
            className="flex items-center gap-3 w-full text-left hover:opacity-80 transition-opacity"
          >
            <Edit2 size={16} className="text-text-muted" />
            <h2 className="text-[0.68rem] tracking-[0.25em] uppercase font-bold text-text-main flex-1">Static Pages</h2>
            <ChevronDown size={16} className={cn("text-text-muted transition-transform", !expandedSections['static-pages'] && "-rotate-90")} />
          </button>
          
          <AnimatePresence initial={false}>
            {expandedSections['static-pages'] && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="bg-bg-card border border-border-secondary rounded-xl p-6 space-y-8">
                  <p className="text-[0.65rem] text-text-muted tracking-widest italic">Maintain About Us, Privacy Policy, and Terms of Service pages.</p>
                  
                  <div className="space-y-12">
                    {['about', 'privacy', 'terms'].map(pageId => (
                      <PageEditor 
                        key={pageId} 
                        pageId={pageId} 
                        page={pages.find(p => p.id === pageId)} 
                        savePage={savePage} 
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
        )}

        {activeSection === 'questions' && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {(['responsibility', 'selftrust', 'care'] as ('responsibility' | 'selftrust' | 'care')[]).map(baseCat => {
            const catQuestions = questions.filter(q => q.category === baseCat || q.category === `shadow_${baseCat}`);
            const sectionId = `questions-${baseCat}`;
            const isExpanded = expandedSections[sectionId];

            return (
              <section key={baseCat} className="space-y-6">
                <button 
                  onClick={() => toggleSection(sectionId)}
                  className="flex justify-between items-baseline w-full text-left hover:opacity-80 transition-opacity"
                >
                  <div className="flex items-center gap-3">
                    <h2 className={cn("text-[0.68rem] tracking-[0.25em] uppercase font-bold", baseCat === 'responsibility' ? 'text-cat-responsibility' : baseCat === 'selftrust' ? 'text-cat-selftrust' : 'text-cat-care')}>
                      {baseCat.replace('selftrust', 'Self Trust')}
                    </h2>
                    <ChevronDown size={14} className={cn("text-text-muted transition-transform", !isExpanded && "-rotate-90")} />
                  </div>
                  <span className="text-[0.68rem] text-text-muted tracking-widest">{catQuestions.length} questions</span>
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden space-y-6"
                    >
                      <div className="bg-bg-card border border-border-secondary rounded-xl p-4 space-y-4">
                        <div className="space-y-2">
                          <label className="text-[0.55rem] tracking-widest uppercase text-text-muted">Short Description (Shown under sliders)</label>
                          <input 
                            type="text"
                            className="w-full bg-bg-input border border-border-secondary rounded-lg p-3 text-xs text-text-main outline-none focus:border-border-focus leading-relaxed" 
                            placeholder={`Short summary of ${baseCat.replace('selftrust', 'Self Trust')}...`}
                            value={appConfig.categoryShortDescriptions?.[baseCat] || ''}
                            onChange={(e) => setAppConfig(prev => ({
                              ...prev,
                              categoryShortDescriptions: {
                                ...(prev.categoryShortDescriptions || { responsibility: '', selftrust: '', care: '', overall: '' }),
                                [baseCat]: e.target.value
                              }
                            }))}
                            onBlur={(e) => saveCategoryShortDescription(baseCat, e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-[0.55rem] tracking-widest uppercase text-text-muted">Full Description (Shown in results)</label>
                          <textarea 
                            value={appConfig.categoryDescriptions?.[baseCat] || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAppConfig(prev => ({
                                ...prev,
                                categoryDescriptions: {
                                  ...(prev.categoryDescriptions || { responsibility: '', selftrust: '', care: '', overall: '' }),
                                  [baseCat]: val
                                }
                              }))
                            }}
                            onBlur={() => saveCategoryDescription(baseCat, appConfig.categoryDescriptions?.[baseCat] || '')}
                            placeholder={`Explain what ${baseCat.replace('selftrust', 'Self Trust')} is...`}
                            className="w-full min-h-[100px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                          />
                        </div>
                        <p className="text-[0.55rem] text-text-muted tracking-widest italic">The short description appears during the assessment, while the full description appears in the final report.</p>
                      </div>

                      <div className="space-y-2">
                        <SortableContext
                          items={catQuestions.map(q => q.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {catQuestions.map(q => (
                            <SortableQuestion
                              key={q.id}
                              q={q}
                              editingQ={editingQ}
                              editingDesc={editingDesc}
                              editingFeedbackQ={editingFeedbackQ}
                              setEditingQ={setEditingQ}
                              setEditingDesc={setEditingDesc}
                              setEditingFeedbackQ={setEditingFeedbackQ}
                              saveQuestionEdit={saveQuestionEdit}
                              deleteQuestion={deleteQuestion}
                              toggleShadow={toggleShadow}
                            />
                          ))}
                        </SortableContext>
                        <div className="bg-bg-card border border-border-secondary rounded-xl p-4 space-y-3">
                          <div className="flex gap-3 items-center">
                            <input type="text" placeholder={`Add a ${baseCat.replace('selftrust', 'self trust')} question...`} className="flex-1 bg-bg-input border border-border-secondary rounded-lg px-4 py-2 text-sm outline-none focus:border-border-focus transition-all" value={newQText[baseCat]} onChange={(e) => setNewQText(prev => ({ ...prev, [baseCat]: e.target.value }))} />
                            <button onClick={() => addQuestion(baseCat)} className={cn("px-5 py-2 rounded-lg text-xs tracking-widest uppercase font-bold transition-all", baseCat === 'responsibility' ? 'bg-cat-responsibility/15 text-cat-responsibility border border-cat-responsibility/30 hover:bg-cat-responsibility/25' : baseCat === 'selftrust' ? 'bg-cat-selftrust/15 text-cat-selftrust border border-cat-selftrust/30 hover:bg-cat-selftrust/25' : 'bg-cat-care/15 text-cat-care border border-cat-care/30 hover:bg-cat-care/25')}>Add</button>
                          </div>
                          <textarea 
                            value={newQDesc[baseCat]} 
                            onChange={(e) => setNewQDesc(prev => ({ ...prev, [baseCat]: e.target.value }))}
                            placeholder="Optional description..." 
                            className="w-full min-h-[80px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            );
          })}
        </DndContext>
        )}

        {activeSection === 'feedback' && (
        <section className="space-y-8">
          <div 
            className="flex justify-between items-end w-full text-left transition-opacity"
          >
            <div 
              onClick={() => toggleSection('score-feedback')}
              className="flex-1 space-y-1 cursor-pointer hover:opacity-80"
            >
              <div className="flex items-center gap-3">
                <h2 className="text-[0.68rem] tracking-[0.25em] uppercase text-text-muted">Score Feedback</h2>
                <ChevronDown size={14} className={cn("text-text-muted transition-transform", !expandedSections['score-feedback'] && "-rotate-90")} />
              </div>
              <p className="text-[0.68rem] text-text-muted tracking-widest">Personalised messages shown to users after assessment</p>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  addFeedbackRange(activeTab);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-bg-input border border-border-secondary rounded-lg text-text-muted hover:bg-bg-card transition-all uppercase tracking-widest text-[0.65rem]"
              >
                <Plus size={14} /> Add Range
              </button>
            </div>
          </div>

          <AnimatePresence initial={false}>
            {expandedSections['score-feedback'] && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden space-y-8"
              >

          <div className="bg-bg-card border border-border-secondary rounded-xl p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">Global Score Insight (Shown under Agency Score)</label>
              <input 
                type="text"
                value={appConfig.scoreInsight || ''} 
                onChange={(e) => {
                  const val = e.target.value;
                  setAppConfig(prev => ({ ...prev, scoreInsight: val }));
                }}
                onBlur={async () => {
                  try {
                    await updateDoc(doc(db, 'config', 'app'), { scoreInsight: appConfig.scoreInsight || '' });
                  } catch (error) {
                    console.error("Error saving score insight:", error);
                  }
                }}
                placeholder={`E.g. "High Agency", "Emerging Agency"...`} 
                className="w-full bg-bg-input border border-border-secondary rounded-lg px-4 py-2 text-sm text-text-main outline-none focus:border-border-focus transition-all"
              />
              <p className="text-[0.55rem] text-text-muted tracking-widest italic">This message is static and appears under the Agency Score regardless of the actual score.</p>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {(['responsibility', 'selftrust', 'care', 'overall'] as Category[]).map(cat => (
              <button key={cat} onClick={() => setActiveTab(cat)} className={cn(
                "px-5 py-2.5 rounded-lg text-[0.72rem] tracking-widest uppercase transition-all border", 
                activeTab === cat ? (
                  cat.includes('responsibility') ? 'bg-cat-responsibility/10 text-cat-responsibility border-cat-responsibility/40' : 
                  cat.includes('selftrust') ? 'bg-cat-selftrust/10 text-cat-selftrust border-cat-selftrust/40' : 
                  cat.includes('care') ? 'bg-cat-care/10 text-cat-care border-cat-care/40' : 
                  'bg-text-main/10 text-text-main border-text-main/40'
                ) : 'bg-bg-card border-border-secondary text-text-muted hover:text-text-main hover:border-border-focus'
              )}>
                {cat.replace('shadow_', 'Shadow ').replace('selftrust', 'Self Trust')}
              </button>
            ))}
          </div>

          <div className="bg-bg-card border border-border-secondary rounded-xl p-4 space-y-4">
            <div className="space-y-2">
              <label className="text-[0.55rem] tracking-widest uppercase text-text-muted">{activeTab.replace('shadow_', 'Shadow ').replace('selftrust', 'Self Trust')} Short Description</label>
              <input 
                type="text"
                className="w-full bg-bg-input border border-border-secondary rounded-lg p-3 text-xs text-text-main outline-none focus:border-border-focus leading-relaxed" 
                placeholder={`Short summary of ${activeTab.replace('shadow_', 'Shadow ').replace('selftrust', 'Self Trust')}...`}
                value={appConfig.categoryShortDescriptions?.[activeTab] || ''}
                onChange={(e) => setAppConfig(prev => ({
                  ...prev,
                  categoryShortDescriptions: {
                    ...(prev.categoryShortDescriptions || { responsibility: '', selftrust: '', care: '', overall: '' }),
                    [activeTab]: e.target.value
                  }
                }))}
                onBlur={(e) => saveCategoryShortDescription(activeTab, e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-[0.55rem] tracking-widest uppercase text-text-muted">{activeTab.replace('shadow_', 'Shadow ').replace('selftrust', 'Self Trust')} Full Description</label>
              <textarea 
                value={appConfig.categoryDescriptions?.[activeTab] || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setAppConfig(prev => ({
                    ...prev,
                    categoryDescriptions: {
                      ...(prev.categoryDescriptions || { responsibility: '', selftrust: '', care: '', overall: '' }),
                      [activeTab]: val
                    }
                  }))
                }}
                onBlur={() => saveCategoryDescription(activeTab, appConfig.categoryDescriptions?.[activeTab] || '')}
                placeholder={`Explain what ${activeTab.replace('shadow_', 'Shadow ').replace('selftrust', 'Self Trust')} is...`}
                className="w-full min-h-[100px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
              />
            </div>
            <p className="text-[0.55rem] text-text-muted tracking-widest italic">The short description appears during the assessment, while the full description appears in the final report.</p>
          </div>

          <div className="space-y-3">
            {feedback.filter(f => f.category === activeTab).sort((a, b) => a.minScore - b.minScore).map((data) => {
              const isOpen = data.id ? openBands[data.id] : false;

              return (
                <div key={data.id} className={cn("bg-bg-card border border-border-secondary rounded-xl overflow-hidden transition-all", isOpen && "border-border-focus")}>
                  <div className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-bg-input transition-colors group">
                    <div onClick={() => data.id && toggleBand(data.id)} className="flex-1 flex items-center gap-4">
                      <span className={cn(
                        "text-[0.68rem] tracking-widest font-bold px-3 py-1 rounded-[5px] flex items-center gap-2", 
                        activeTab.includes('responsibility') ? 'bg-cat-responsibility/15 text-cat-responsibility' : 
                        activeTab.includes('selftrust') ? 'bg-cat-selftrust/15 text-cat-selftrust' : 
                        activeTab.includes('care') ? 'bg-cat-care/15 text-cat-care' : 
                        'bg-text-main/15 text-text-main'
                      )}>
                        {activeTab === 'overall' ? (
                          <span>{data.title || 'Untitled Range'}</span>
                        ) : (
                          <span>{data.minScore} - {data.maxScore}</span>
                        )}
                        {activeTab === 'overall' && (
                          <span className="text-[0.55rem] opacity-60 border-l border-current pl-2">
                            R: {data.responsibilityMin}-{data.responsibilityMax} | S: {data.selftrustMin}-{data.selftrustMax} | C: {data.careMin}-{data.careMax}
                          </span>
                        )}
                      </span>
                      <span className="flex-1 text-[0.82rem] text-text-main">
                        {data.text ? <Markdown className="markdown-body" content={data.text.substring(0, 60) + (data.text.length > 60 ? '...' : '')} /> : <span className="text-text-muted/60 italic">No feedback text</span>}
                      </span>
                      <span className="text-[0.72rem] text-text-muted">{data.resources.length > 0 ? `${data.resources.length} resources` : ''}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => data.id && deleteFeedback(data.id)} className="p-2 text-text-muted hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                      <span onClick={() => data.id && toggleBand(data.id)} className={cn("text-text-muted transition-transform", isOpen && "rotate-180")}><ChevronDown size={14} /></span>
                    </div>
                  </div>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="px-6 py-6 border-t border-border-secondary space-y-6 overflow-hidden"
                      >
                      <div className="space-y-2">
                        <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">Range Title</label>
                        <input 
                          type="text" 
                          className="w-full bg-bg-input border border-border-secondary rounded-lg px-4 py-2 text-sm text-text-main outline-none focus:border-border-focus" 
                          defaultValue={data.title} 
                          onBlur={(e) => saveFeedback(data.id, { title: e.target.value })} 
                          placeholder="e.g. High Agency, Balanced, etc."
                        />
                      </div>

                      {activeTab !== 'overall' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">Min Score</label>
                            <input type="number" step="0.1" min="0" max={5} className="w-full bg-bg-input border border-border-secondary rounded-lg px-4 py-2 text-sm text-text-main outline-none focus:border-border-focus" defaultValue={data.minScore} onBlur={(e) => saveFeedback(data.id, { minScore: parseFloat(e.target.value) })} />
                          </div>
                          <div className="space-y-2">
                            <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">Max Score</label>
                            <input type="number" step="0.1" min="0" max={5} className="w-full bg-bg-input border border-border-secondary rounded-lg px-4 py-2 text-sm text-text-main outline-none focus:border-border-focus" defaultValue={data.maxScore} onBlur={(e) => saveFeedback(data.id, { maxScore: parseFloat(e.target.value) })} />
                          </div>
                        </div>
                      )}

                      {activeTab === 'overall' && (
                        <div className="space-y-6 pt-4 border-t border-border-secondary">
                          <p className="text-[0.6rem] tracking-[0.2em] uppercase text-text-muted font-bold">Category Specific Requirements</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-3">
                              <p className="text-[0.6rem] tracking-widest uppercase text-cat-responsibility font-semibold">Responsibility</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-[0.5rem] uppercase text-text-muted">Min</label>
                                  <input type="number" step="0.1" min="0" max="5" className="w-full bg-bg-input border border-border-secondary rounded-lg px-3 py-1.5 text-xs text-text-main outline-none focus:border-border-focus" defaultValue={data.responsibilityMin ?? 0} onBlur={(e) => saveFeedback(data.id, { responsibilityMin: parseFloat(e.target.value) })} />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[0.5rem] uppercase text-text-muted">Max</label>
                                  <input type="number" step="0.1" min="0" max="5" className="w-full bg-bg-input border border-border-secondary rounded-lg px-3 py-1.5 text-xs text-text-main outline-none focus:border-border-focus" defaultValue={data.responsibilityMax ?? 5} onBlur={(e) => saveFeedback(data.id, { responsibilityMax: parseFloat(e.target.value) })} />
                                </div>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <p className="text-[0.6rem] tracking-widest uppercase text-cat-selftrust font-semibold">Self Trust</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-[0.5rem] uppercase text-text-muted">Min</label>
                                  <input type="number" step="0.1" min="0" max="5" className="w-full bg-bg-input border border-border-secondary rounded-lg px-3 py-1.5 text-xs text-text-main outline-none focus:border-border-focus" defaultValue={data.selftrustMin ?? 0} onBlur={(e) => saveFeedback(data.id, { selftrustMin: parseFloat(e.target.value) })} />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[0.5rem] uppercase text-text-muted">Max</label>
                                  <input type="number" step="0.1" min="0" max="5" className="w-full bg-bg-input border border-border-secondary rounded-lg px-3 py-1.5 text-xs text-text-main outline-none focus:border-border-focus" defaultValue={data.selftrustMax ?? 5} onBlur={(e) => saveFeedback(data.id, { selftrustMax: parseFloat(e.target.value) })} />
                                </div>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <p className="text-[0.6rem] tracking-widest uppercase text-cat-care font-semibold">Care</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <label className="text-[0.5rem] uppercase text-text-muted">Min</label>
                                  <input type="number" step="0.1" min="0" max="5" className="w-full bg-bg-input border border-border-secondary rounded-lg px-3 py-1.5 text-xs text-text-main outline-none focus:border-border-focus" defaultValue={data.careMin ?? 0} onBlur={(e) => saveFeedback(data.id, { careMin: parseFloat(e.target.value) })} />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[0.5rem] uppercase text-text-muted">Max</label>
                                  <input type="number" step="0.1" min="0" max="5" className="w-full bg-bg-input border border-border-secondary rounded-lg px-3 py-1.5 text-xs text-text-main outline-none focus:border-border-focus" defaultValue={data.careMax ?? 5} onBlur={(e) => saveFeedback(data.id, { careMax: parseFloat(e.target.value) })} />
                                </div>
                              </div>
                            </div>
                          </div>
                          <p className="text-[0.55rem] text-text-muted tracking-widest italic">For overall feedback, all category ranges must be met simultaneously.</p>
                        </div>
                      )}
                      <div className="space-y-2">
                        <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">Feedback message</label>
                        <textarea 
                          value={data.text} 
                          onChange={(e) => {
                            const val = e.target.value;
                            setFeedback(prev => prev.map(f => f.id === data.id ? { ...f, text: val } : f));
                          }}
                          onBlur={() => saveFeedback(data.id, { text: data.text })}
                          placeholder={`Write feedback for this range...`} 
                          className="w-full min-h-[120px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[0.68rem] tracking-widest uppercase text-cat-responsibility">How to Level Up</label>
                        <textarea 
                          value={data.levelUpText} 
                          onChange={(e) => {
                            const val = e.target.value;
                            setFeedback(prev => prev.map(f => f.id === data.id ? { ...f, levelUpText: val } : f));
                          }}
                          onBlur={() => saveFeedback(data.id, { levelUpText: data.levelUpText })}
                          placeholder={`Advice on how to improve this score...`} 
                          className="w-full min-h-[100px] bg-bg-input border border-border-secondary rounded-lg p-3 text-sm text-text-main outline-none focus:border-border-focus transition-all resize-y"
                        />
                        <p className="text-[0.6rem] text-text-muted tracking-widest italic">Actionable steps for the user to increase their score in this category.</p>
                      </div>
                      <div className="space-y-3">
                        <label className="text-[0.68rem] tracking-widest uppercase text-text-muted">Resources</label>
                        <div className="space-y-2">
                          {data.resources.map((res, i) => (
                            <div key={i} className="flex gap-2">
                              <input className="flex-[0.4] bg-bg-input border border-border-secondary rounded-lg px-3 py-2 text-xs text-text-main outline-none focus:border-border-focus" placeholder="Label" defaultValue={res.label} onBlur={(e) => {
                                const newRes = [...data.resources];
                                newRes[i] = { ...newRes[i], label: e.target.value };
                                saveFeedback(data.id, { resources: newRes });
                              }} />
                              <input className="flex-1 bg-bg-input border border-border-secondary rounded-lg px-3 py-2 text-xs text-text-main outline-none focus:border-border-focus" placeholder="URL" defaultValue={res.url} onBlur={(e) => {
                                const newRes = [...data.resources];
                                newRes[i] = { ...newRes[i], url: e.target.value };
                                saveFeedback(data.id, { resources: newRes });
                              }} />
                              <button onClick={() => {
                                const newRes = data.resources.filter((_, idx) => idx !== i);
                                saveFeedback(data.id, { resources: newRes });
                              }} className="p-2 text-text-muted hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                            </div>
                          ))}
                          <button onClick={() => {
                            const newRes = [...data.resources, { label: '', url: '' }];
                            saveFeedback(data.id, { resources: newRes });
                          }} className="w-full py-2.5 border border-dashed border-border-secondary rounded-xl text-[0.72rem] text-text-muted hover:text-text-main hover:border-border-focus transition-all tracking-widest uppercase">+ Add resource link</button>
                        </div>
                      </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
            {feedback.filter(f => f.category === activeTab).length === 0 && (
              <div className="text-center py-12 bg-bg-card border border-dashed border-border-secondary rounded-xl">
                <p className="text-[0.72rem] text-text-muted tracking-widest uppercase">No feedback ranges defined for this category</p>
              </div>
            )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
        )}
        </div>
      </main>
    </div>
  );
};
